/* eslint-disable import/prefer-default-export,max-len */
import { MsalAuthProvider, LoginType, AuthenticationState } from 'react-aad-msal';
import { Logger, LogLevel } from 'msal';

const b2cPolicies = {
  authorities: {
    signIn: {
      authority: process.env.REACT_APP_AAD_AUTHORITY_URL_LOGIN,
    },
    forgotPassword: {
      authority: process.env.REACT_APP_AAD_AUTHORITY_URL_RESET_PASSWORD,
    },
  },
};

// Msal Configurations
const authProvider = new MsalAuthProvider({
  auth: {
    authority: b2cPolicies.authorities.signIn.authority,
    clientId: process.env.REACT_APP_AAD_AUTHORITY_CLIENTID,
    // window.location.href capturaba la URL con el hash #id_token al volver del
    // login por redirect; B2C la rechazaba como post_logout_redirect_uri.
    // Se usa la reply URL registrada en B2C.
    postLogoutRedirectUri: process.env.REACT_APP_AAD_REDIRECT_URL,
    redirectUri: process.env.REACT_APP_AAD_REDIRECT_URL,
    validateAuthority: false,
  },
  system: {
    logger: new Logger(
      (logLevel, message, containsPii) => {
        console.log('[MSAL]', message);
      },
      {
        level: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    ),
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
}, {
  scopes: [`${process.env.REACT_APP_AAD_AUTHORITY_URL_IMPERSONATION}`],
}, {
  // Redirect en lugar de Popup: Firefox bloquea el window.open asincrono de MSAL v1
  // y el popup quedaba abierto sin completar el login.
  loginType: LoginType.Redirect,
  tokenRefreshUri: `${window.location.origin}/auth.html`,
});

const handleError = (error) => {
  // console.log('handleError: ', error);
  if (error?.errorMessage.indexOf('AADB2C90118') > -1) {
    authProvider.setAuthenticationParameters({ authority: b2cPolicies.authorities.forgotPassword.authority });
    authProvider.login();
  }
  // Renewal token exception is thrown so will be necessary to launch login flow again. It's error happen on forgot password user flow
  if (error?.errorCode === 'token_renewal_error') {
    authProvider.setAuthenticationParameters({ authority: b2cPolicies.authorities.signIn.authority });
    // Con LoginType.Redirect, login() redirige la pagina completa. La renovacion
    // silenciosa falla siempre en Firefox (ETP bloquea el iframe de B2C), asi que
    // solo se relanza el login si habia sesion o interaccion en curso; un usuario
    // no autenticado no debe ser expulsado de la ruta publica donde navega.
    if (authProvider.authenticationState !== AuthenticationState.Unauthenticated) {
      authProvider.login();
    }
  }
};

authProvider.registerErrorHandler(handleError);

// MSAL v1 + LoginType.Redirect: cuando la renovacion silenciosa del token requiere
// interaccion (siempre en Firefox, cuyo ETP bloquea el iframe de B2C), la libreria
// hace acquireTokenRedirect y manda toda la pagina a B2C — incluso para visitantes
// sin sesion en rutas publicas. Se anula ese auto-redirect: una sesion expirada o
// una cuenta residual en cache queda como no autenticada y el login vuelve a ser
// una accion explicita del usuario (con SSO de B2C el re-login es inmediato).
authProvider.loginToRefreshToken = (error) => {
  authProvider.setAuthenticationState(AuthenticationState.Unauthenticated);
  return Promise.reject(error);
};

const decodeJwtClaims = (rawToken) => {
  try {
    const payload = rawToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(payload));
  } catch (e) {
    return null;
  }
};

// getIdToken de react-aad-msal ignora el id_token recien guardado por el redirect
// y siempre renueva via iframe ("No matching tokens found" + RenewIdToken); en
// Firefox ese iframe esta bloqueado y el login nunca se completaba. Si el id_token
// cacheado por MSAL sigue vigente, se usa directamente sin tocar el iframe.
const renewIdTokenViaIframe = authProvider.getIdToken;
authProvider.getIdToken = (parameters) => {
  const rawIdToken = window.sessionStorage.getItem('msal.idtoken');
  const claims = rawIdToken ? decodeJwtClaims(rawIdToken) : null;
  const stillValid = claims?.exp && claims.exp * 1000 > Date.now() + 60000;
  if (stillValid) {
    const response = {
      tokenType: 'id_token',
      idToken: { rawIdToken, claims },
      account: authProvider.getAccount(),
      accountState: '',
    };
    authProvider.handleAcquireTokenSuccess(response);
    authProvider.setAuthenticationState(AuthenticationState.Authenticated);
    return Promise.resolve({ idToken: response.idToken, state: '' });
  }
  return renewIdTokenViaIframe(parameters);
};

export { authProvider };
